import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './aboutFans.css';
import { openQuoteChat, scrollPageTop } from '../../../utils/site';
import MarketingPageLayout from '../../common/layout/MarketingPageLayout';
import FeatureCardsSection from '../../common/marketing/FeatureCardsSection';
import ProductShowcaseSection from '../../common/marketing/ProductShowcaseSection';
import StatsSection from '../../common/marketing/StatsSection';
import ContentCardsSection from '../../common/marketing/ContentCardsSection';
import FaqSectionBlock from '../../common/marketing/FaqSectionBlock';
import CtaSection from '../../common/marketing/CtaSection';
import { getSegmentPageData, getCollectionPageData } from '../../../data/db';
import pageAssets from '../../../data/pageAssets';
import AppImage from '../../common/media/AppImage';
import FansSeo from './components/FansSeo';
import RouteConstants from '../../../utils/RouteConstants';
import FansHeroSection from './components/FansHeroSection';

const buildSeriesCatalog = (data) => {
    const catalog = data.seriesCatalog || [];
    return catalog.map((series) => {
        const source = data[series.sourceKey];
        const items = source?.items || source || [];
        const models = items.map((fan) => ({
            ...fan,
            seriesTitle: series.eyebrow,
            ...(series.generateDescription && {
                description: `${fan.name} pairs ${fan.color.toLowerCase()} styling with ${fan.rpm.toLowerCase()} performance and ${fan.motor.toLowerCase()}.`
            }),
            chips: series.chipFields.map((field) => fan[field])
        }));
        return {
            id: series.id,
            eyebrow: series.eyebrow,
            title: series.title,
            badge: `${items.length} Models`,
            description: series.description || source?.description || '',
            highlights: series.highlights,
            models
        };
    });
};

const buildCatalogRows = (seriesCatalog) => seriesCatalog.flatMap((series) =>
    series.models.map((fan) => ({
        key: `${series.id}-${fan.name}-${fan.sweep || fan.color}`,
        name: fan.name,
        series: series.eyebrow,
        title: series.title,
        imageKey: fan.imageKey,
        finish: fan.color || fan.finish,
        specs: fan.chips,
        description: fan.description
    }))
);

const getCompactDescription = (text) => {
    const firstSentence = text.split('. ')[0];
    return firstSentence.endsWith('.') ? firstSentence : `${firstSentence}.`;
};

const AboutFans = () => {
    useEffect(() => {
        scrollPageTop();
    }, []);

    const fanData = getSegmentPageData('fans');
    const collectionData = getCollectionPageData('fans');
    const {
        seo,
        hero,
        featuresSection,
        specsSection,
        certifications: certificationsSection,
        buyingGuide: buyingGuideSection,
        faq: faqSection,
        cta: ctaSection,
        structuredProducts
    } = fanData;
    const amazer = fanData.secondary;
    const seriesCatalog = buildSeriesCatalog(collectionData);
    const catalogRows = buildCatalogRows(seriesCatalog);
    
    return (
        <>
            <FansSeo seo={seo} faqItems={faqSection.items} structuredProducts={structuredProducts} buyingGuide={buyingGuideSection} catalogRows={catalogRows} />
            <MarketingPageLayout pageClassName="about-fans-page">
                <FansHeroSection hero={hero} />
                <FeatureCardsSection
                    sectionClassName="about-fans-features"
                    sectionId="features"
                    header={{
                        wrapperClassName: 'about-fans-section-header',
                        subtitleClassName: 'about-fans-section-subtitle',
                        titleClassName: 'about-fans-section-title',
                        subtitle: featuresSection.subtitle,
                        title: featuresSection.title
                    }}
                    gridClassName="about-fans-features-grid"
                    cardClassName="about-fans-feature-card"
                    iconClassName="about-fans-feature-icon"
                    items={featuresSection.items}
                />
                <ProductShowcaseSection
                    sectionClassName="amazer-section"
                    sectionId="amazer"
                    sectionHeader={{
                        wrapperClassName: 'about-fans-section-header',
                        subtitleClassName: 'about-fans-section-subtitle',
                        titleClassName: 'about-fans-section-title',
                        descriptionClassName: 'amazer-section-desc',
                        subtitle: amazer.subtitle,
                        title: amazer.title,
                        description: amazer.description
                    }}
                    gridClassName="amazer-showcase-grid"
                    imageClassName="amazer-showcase-image"
                    contentClassName="amazer-showcase-content"
                    subtitleClassName="about-fans-section-subtitle"
                    bodyClassName="amazer-showcase-text"
                    data={{
                        imageKey: amazer.imageKey,
                        imageAlt: amazer.imageAlt,
                        subtitle: amazer.panelSubtitle,
                        titlePrefix: amazer.panelTitlePrefix,
                        titleHighlight: amazer.panelTitleHighlight,
                        titleSuffix: amazer.panelTitleSuffix,
                        description: amazer.panelDescription,
                        highlights: amazer.highlights
                    }}
                    cta={{ type: 'button', className: 'btn btn-outline btn-lg', label: 'View Collection', onClick: () => document.getElementById('fan-catalog')?.scrollIntoView({ behavior: 'smooth' }) }}
                    highlightListClassName="about-fans-highlights"
                    highlightItemClassName="about-fans-highlight-item"
                    highlightIconClassName="about-fans-highlight-check"
                />
                <section id="fan-catalog" className="fan-series-section">
                    <div className="container">
                        <div className="fan-section-heading fan-series-intro">
                            <div className="fan-section-eyebrow">Fan Series Catalog</div>
                            <h2>Choose The Right Series</h2>
                            <p>Browse our complete ceiling fan collection across decorative, classic and compact categories.</p>
                        </div>

                        <div className="fan-series-stack">
                            {seriesCatalog.map((series) => (
                                <article key={series.id} id={`series-${series.id}`} className="fan-series-panel">
                                    <div className="fan-series-header">
                                        <div className="fan-series-copy">
                                            <div className="fan-section-eyebrow">{series.eyebrow}</div>
                                            <h3>{series.title}</h3>
                                            <p>{series.description}</p>
                                        </div>
                                        <div className="fan-series-header-chips">
                                            {series.highlights.map((item) => (
                                                <span key={`${series.id}-head-${item}`} className="fan-series-header-chip">{item}</span>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="fan-series-model-list">
                                        {series.models.map((fan) => (
                                            <article key={`${series.id}-${fan.name}-${fan.imageKey}`} className="fan-series-model-card">
                                                <div className="fan-series-model-image">
                                                    <AppImage src={pageAssets[fan.imageKey]} alt={`NISHAD ${fan.name} Ceiling Fan`} />
                                                </div>
                                                <div className="fan-series-model-content">
                                                    <div className="fan-series-model-meta">
                                                        <span className="fan-series-model-label">{series.eyebrow}</span>
                                                        {fan.badge ? <span className="fan-series-model-badge">{fan.badge}</span> : null}
                                                    </div>
                                                    <h3>{fan.name}</h3>
                                                    <div className="fan-series-model-finish">Finish: {fan.color || fan.finish}</div>
                                                    <p>{getCompactDescription(fan.description)}</p>
                                                    <div className="fan-series-model-chips fan-series-model-chips--spaced">
                                                        {fan.chips.map((item) => (
                                                            <span key={`${series.id}-${fan.name}-${item}`} className="fan-series-model-chip">{item}</span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </article>
                                        ))}
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>
                <StatsSection
                    sectionClassName="about-fans-specs"
                    header={{
                        wrapperClassName: 'about-fans-section-header',
                        subtitleClassName: 'about-fans-section-subtitle',
                        titleClassName: 'about-fans-section-title',
                        subtitle: specsSection.subtitle,
                        title: specsSection.title
                    }}
                    gridClassName="about-fans-specs-grid"
                    cardClassName="about-fans-spec-card"
                    valueClassName="about-fans-spec-value"
                    labelClassName="about-fans-spec-label"
                    items={specsSection.items}
                />
                <ContentCardsSection
                    sectionClassName="about-fans-trust"
                    header={{
                        wrapperClassName: 'about-fans-section-header',
                        subtitleClassName: 'about-fans-section-subtitle',
                        titleClassName: 'about-fans-section-title',
                        subtitle: certificationsSection.subtitle,
                        title: certificationsSection.title
                    }}
                    gridClassName="about-fans-trust-grid"
                    cardClassName="about-fans-trust-card"
                    iconClassName="about-fans-trust-icon"
                    items={certificationsSection.items}
                />
                <ContentCardsSection
                    sectionClassName="about-fans-trust"
                    header={{
                        wrapperClassName: 'about-fans-section-header',
                        subtitleClassName: 'about-fans-section-subtitle',
                        titleClassName: 'about-fans-section-title',
                        descriptionClassName: 'faq-section-desc',
                        subtitle: buyingGuideSection.subtitle,
                        title: buyingGuideSection.title,
                        description: buyingGuideSection.description
                    }}
                    gridClassName="about-fans-trust-grid"
                    cardClassName="about-fans-trust-card about-fans-trust-card--text"
                    items={buyingGuideSection.items}
                />
                <FaqSectionBlock
                    sectionClassName="faq-section"
                    sectionId="faq"
                    header={{
                        wrapperClassName: 'about-fans-section-header',
                        subtitleClassName: 'about-fans-section-subtitle',
                        titleClassName: 'about-fans-section-title',
                        descriptionClassName: 'faq-section-desc',
                        subtitle: faqSection.subtitle,
                        title: faqSection.title,
                        description: faqSection.description
                    }}
                    faqProps={{
                        items: faqSection.items,
                        listClassName: 'faq-list',
                        itemClassName: 'faq-item',
                        questionClassName: 'faq-question',
                        questionTextClassName: 'faq-question-text',
                        iconClassName: 'faq-icon',
                        answerClassName: 'faq-answer'
                    }}
                />
                <section className="about-fans-cross-link">
                    <div className="container">
                        <p>Also explore our <Link to={RouteConstants.WIRES} className="cross-link-anchor">Electrical Wires & Cables</Link> range — ISI-certified copper wires for safe, durable home wiring.</p>
                    </div>
                </section>
                <CtaSection
                    sectionClassName="about-fans-cta"
                    title={ctaSection.title}
                    description={ctaSection.description}
                    buttonClassName="about-fans-cta-btn"
                    buttonLabel={ctaSection.buttonLabel}
                    onClick={openQuoteChat}
                />
            </MarketingPageLayout>
        </>
    );
};

export default AboutFans;
